import { Transformers } from './transformers';

/**
 * Recursive GraphQL response walker — applies field-level transformers
 * (unit conversions, date parsing) based on each object's __typename.
 *
 * Traversal order matters:
 * 1. null/undefined → return as-is (no transformation needed)
 * 2. Array → recurse into each element
 * 3. Date instance → return as-is (short-circuit before the object branch
 *    because Date is typeof 'object' but should not be walked as a data node)
 * 4. Object with __typename → look up field transformers and apply them
 * 5. Nested objects without transformers → recurse to find deeper __typename nodes
 */
export function applyTransformers(data: unknown, transformers: Transformers): unknown {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) return data.map((item) => applyTransformers(item, transformers));
  if (typeof data !== 'object' || data instanceof Date) return data;

  const record = data as Record<string, unknown>;
  const typeName = record.__typename as string | undefined;
  const fieldTransformers = typeName ? transformers[typeName] : undefined;
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(record)) {
    const value = record[key];
    if (fieldTransformers && fieldTransformers[key]) {
      result[key] = fieldTransformers[key].parseValue(value);
    } else if (typeof value === 'object' && value !== null) {
      result[key] = applyTransformers(value, transformers);
    } else {
      result[key] = value;
    }
  }

  return result;
}
